import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bell, Trash2, MessageCircle, Send } from "lucide-react";

const fmt4 = (n) => Number(n ?? 0).toLocaleString("tr-TR", { minimumFractionDigits: 4, maximumFractionDigits: 4 });

const empty = {
  channel: "whatsapp", recipient: "", metal_type: "gold",
  condition: "above", threshold: 3300, message: "", active: true,
};

export default function Notifications() {
  const [rules, setRules] = useState([]);
  const [log, setLog] = useState([]);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const [r1, r2] = await Promise.all([
      api.get("/notifications/rules"),
      api.get("/notifications/log"),
    ]);
    setRules(r1.data);
    setLog(r2.data);
  };
  useEffect(() => { load(); const iv = setInterval(load, 10000); return () => clearInterval(iv); }, []);

  const add = async () => {
    if (!form.recipient) return toast.error("Alıcı gerekli");
    try {
      await api.post("/notifications/rules", { ...form, id: crypto.randomUUID(), created_at: new Date().toISOString() });
      toast.success("Kural eklendi");
      setForm(empty);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Hata");
    }
  };

  const del = async (id) => { await api.delete(`/notifications/rules/${id}`); load(); };

  return (
    <div className="space-y-8" data-testid="notify-page">
      <div>
        <div className="label-caps">Yatırım Asistanı</div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-2 font-light">Kur Bildirimleri</h1>
        <p className="text-neutral-400 mt-3 max-w-2xl">
          Belirlenen kur eşiklerinde WhatsApp veya Telegram üzerinden VIP müşterilerinize otomatik bildirim.
          <span className="ml-2 label-caps" style={{color:"var(--gold)"}}>Demo mod: bildirimler simüle edilir</span>
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 card-flat p-8">
          <div className="label-caps mb-2">Yeni Kural</div>
          <div className="font-display text-2xl mb-6">Bildirim Ayarla</div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-caps block mb-2">Kanal</label>
                <select value={form.channel} onChange={(e)=>setForm({...form, channel: e.target.value})} className="w-full border border-white/10 px-3 py-2 text-sm" style={{background:"var(--surface-2)"}} data-testid="notif-channel">
                  <option value="whatsapp">WhatsApp</option>
                  <option value="telegram">Telegram</option>
                </select>
              </div>
              <div>
                <label className="label-caps block mb-2">Metal</label>
                <select value={form.metal_type} onChange={(e)=>setForm({...form, metal_type: e.target.value})} className="w-full border border-white/10 px-3 py-2 text-sm" style={{background:"var(--surface-2)"}} data-testid="notif-metal">
                  <option value="gold">Altın</option>
                  <option value="silver">Gümüş</option>
                  <option value="platinum">Platin</option>
                </select>
              </div>
            </div>

            <div>
              <label className="label-caps block mb-2">Alıcı (Telefon / @kullanici)</label>
              <input value={form.recipient} onChange={(e)=>setForm({...form, recipient: e.target.value})} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[--gold]" placeholder="+90 555 000 0000" data-testid="notif-recipient" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-caps block mb-2">Koşul</label>
                <select value={form.condition} onChange={(e)=>setForm({...form, condition: e.target.value})} className="w-full border border-white/10 px-3 py-2 text-sm" style={{background:"var(--surface-2)"}} data-testid="notif-condition">
                  <option value="above">Üstüne çıkarsa</option>
                  <option value="below">Altına düşerse</option>
                </select>
              </div>
              <div>
                <label className="label-caps block mb-2">Eşik (₺/gr)</label>
                <input type="number" step="0.01" value={form.threshold} onChange={(e)=>setForm({...form, threshold: Number(e.target.value)})} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm font-mono tabular-nums focus:outline-none focus:ring-1 focus:ring-[--gold]" data-testid="notif-threshold" />
              </div>
            </div>

            <div>
              <label className="label-caps block mb-2">Mesaj (ops.)</label>
              <textarea rows="2" value={form.message} onChange={(e)=>setForm({...form, message: e.target.value})} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[--gold]" data-testid="notif-message" />
            </div>

            <button onClick={add} className="btn-primary-gold w-full" data-testid="notif-add">Kural Ekle</button>
          </div>
        </div>

        <div className="lg:col-span-7 space-y-6">
          <div className="card-flat p-6">
            <div className="label-caps mb-2">Aktif Kurallar</div>
            <div className="font-display text-2xl mb-4">{rules.length} Kural</div>
            <div className="divide-y divide-white/5">
              {rules.length === 0 && <div className="text-sm text-neutral-500 py-4">Kural yok</div>}
              {rules.map((r) => (
                <div key={r.id} className="flex justify-between items-center py-3" data-testid={`rule-${r.id}`}>
                  <div>
                    <div className="text-sm flex items-center gap-2">
                      {r.channel === "whatsapp" ? <MessageCircle size={14} className="text-emerald-400" /> : <Send size={14} className="text-sky-400" />}
                      <span>{r.recipient}</span>
                      <span className="label-caps">{r.metal_type}</span>
                    </div>
                    <div className="text-xs text-neutral-500 mt-1">
                      {r.condition === "above" ? "Üstü:" : "Altı:"} <span className="font-mono">{fmt4(r.threshold)} ₺</span>
                    </div>
                  </div>
                  <button onClick={()=>del(r.id)} className="text-neutral-400 hover:text-red-400" data-testid={`del-rule-${r.id}`}><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </div>

          <div className="card-flat p-6">
            <div className="label-caps mb-2">Bildirim Geçmişi <span className="text-[--gold] ml-2" style={{color:"var(--gold)"}}>SİMÜLE</span></div>
            <div className="font-display text-2xl mb-4">Son Gönderiler</div>
            <div className="max-h-72 overflow-auto divide-y divide-white/5">
              {log.length === 0 && <div className="text-sm text-neutral-500 py-4">Henüz bildirim yok</div>}
              {log.map((l) => (
                <div key={l.id} className="py-3">
                  <div className="flex justify-between items-baseline">
                    <div className="text-sm">{l.recipient}</div>
                    <div className="label-caps">{new Date(l.sent_at).toLocaleTimeString("tr-TR")}</div>
                  </div>
                  <div className="text-xs text-neutral-400 mt-1">{l.message}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
